import {
    asData,
    externalData,
    type MaybeObservableValue,
    type ObservableValue,
} from '@amazon/vinyl-observable'
import {
    createDisposer,
    createLogPrefix,
    equalDeep,
    first,
    logDebug,
    type LogTarget,
    logWarn,
    type Maybe,
    noop,
    resolveValueProvider,
    type Unsubscribe,
} from '@amazon/vinyl-util'
import { createErrorHandler } from '@/errorHandler'
import {
    isCurrentPlaylistOrContainer,
    type PlaylistController,
} from '@/playback/PlaylistController'
import { type Player } from '@/playback/api'
import {
    createCloudQueue,
    type CreateCloudQueueOptions,
    type CreateCloudQueuePlayOptions,
    type CreateQueueResult,
} from '@/cloudQueue/cloudQueueService'
import { playableEntityToPlaylistEntry } from '@/playback/playableEntityToPlaylistEntry'
import type { PlayableEntity } from '@/service/fragments/playableEntityFrag'
import { type PlayParams } from '@/service/fragments/playParamsFrag'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import { getAuth } from '@/auth/authRef'

export interface PlayQueueBindingDeps {
    readonly playerPromise: Promise<Player>
    readonly playlistController: PlaylistController
    readonly cloudQueueController: CloudQueueController
}

export interface PlayQueueBindingOptions {
    readonly playParams: MaybeObservableValue<Maybe<PlayParams>>

    readonly deps: PlayQueueBindingDeps
}

export enum PlayQueueState {
    NOT_SET = 'notSet',
    LOADING = 'loading',
    PLAYING = 'playing',
    PAUSED = 'paused',
}

export class PlayQueueBinding implements LogTarget {
    public readonly logPrefix = createLogPrefix('PlayQueueBinding')
    readonly errorHandler: (error: any) => void

    // The promise to the player
    private readonly playerPromise: Promise<Player>
    // Set when playerPromise resolves
    private player: Player | null = null
    private readonly playlistController: PlaylistController
    private readonly cloudQueueController: CloudQueueController
    private readonly playParams: ObservableValue<Maybe<PlayParams>>

    private lastCreateQueueOptions: CreateCloudQueueOptions | null = null
    private createQueuePromise: Promise<CreateQueueResult> | null = null
    public readonly state: ObservableValue<PlayQueueState>

    constructor(props: PlayQueueBindingOptions) {
        this.errorHandler = createErrorHandler(this)
        this.playlistController = props.deps.playlistController
        this.cloudQueueController = props.deps.cloudQueueController
        this.playParams = asData(props.playParams)
        this.state = this.createStateProvider()
        this.playerPromise = props.deps.playerPromise
        props.deps.playerPromise
            .then((player) => {
                this.player = player
            })
            .catch(this.errorHandler)
    }

    /**
     * Returns true if the currently playing queue is a container of
     * @private
     */
    private containerIsSet(): boolean {
        return isCurrentPlaylistOrContainer(this.playlistController, this.playParams.value)
    }

    private createStateProvider(): ObservableValue<PlayQueueState> {
        const getCurrentState = () => {
            let newState: PlayQueueState
            if (!this.containerIsSet()) {
                newState = PlayQueueState.NOT_SET
            } else {
                const player = this.player
                if (!player?.hasMetadata) {
                    newState = PlayQueueState.LOADING
                } else {
                    if (player.paused || player.ended) {
                        newState = PlayQueueState.PAUSED
                    } else {
                        newState = PlayQueueState.PLAYING
                    }
                }
            }
            return newState
        }

        return externalData(getCurrentState(), (setData) => {
            const disposer = createDisposer()
            const { add, dispose } = disposer
            const refresh = () => {
                setData(getCurrentState())
            }
            add(this.playlistController.playParams.onData(refresh))
            add(this.playParams.onData(refresh))
            this.playerPromise
                .then((player) => {
                    if (disposer.disposed) return
                    add(onPlaybackStateChange(player, refresh))
                })
                .catch(noop) // Error reported in constructor handler
            return dispose
        })
    }

    /**
     * Prefetches the queue and tracks.
     */
    prefetch(options?: CreateCloudQueuePlayOptions): void {
        if (!getAuth().isSignedIn()) return
        const v = this.playParams.value
        if (!v) return
        this.getOrCreateQueue({
            ...options,
            playParams: v,
        }).catch(this.errorHandler)
    }

    /**
     * Clears the prefetched queue.
     * Does not clear prefetched tracks.
     * Can be used to prevent the next call to setQueue from re-using last queue.
     */
    clearPrefetch() {
        this.createQueuePromise = null
        this.lastCreateQueueOptions = null
    }

    private getOrCreateQueue(options: CreateCloudQueueOptions): Promise<CreateQueueResult> {
        if (!this.createQueuePromise || !equalDeep(options, this.lastCreateQueueOptions)) {
            logDebug(this, 'prefetching queue')

            this.createQueuePromise = this.createQueue(options)
            this.lastCreateQueueOptions = options
        }
        return this.createQueuePromise
    }

    /**
     * If the queue isn't set, it will be set.
     * playback will be toggled.
     */
    togglePlay(options?: CreateCloudQueuePlayOptions): void {
        logDebug(this, 'togglePlay')

        if (!this.containerIsSet()) {
            this.setQueue(options)
        } else {
            const player = this.player
            if (!player) {
                logWarn(this, 'player not set')
                return
            }
            if (player.paused) {
                player.play().catch(this.errorHandler)
            } else {
                player.pause()
            }
        }
    }

    /**
     * Begins playback of an entity. If it is already playing, it will do nothing.
     */
    playEntity(entity: PlayableEntity): void {
        logDebug(this, 'play')
        const playlistCurrent = this.playlistController.current.value
        if (!this.containerIsSet() || entity.id !== playlistCurrent?.entity.id) {
            this.setQueue()
        } else {
            if (!this.player) {
                logWarn(this, 'player not set')
                return
            }
            this.player.play().catch(this.errorHandler)
        }
    }

    /**
     * Sets the playlist to use this queue.
     * Will begin playback.
     */
    setQueue(options?: CreateCloudQueuePlayOptions) {
        if (!getAuth().isSignedIn()) return
        const playParams = this.playParams.value
        logDebug(this, 'setQueue', playParams)
        if (!playParams?.id) {
            logWarn(this, 'playParams not set')
            return
        }
        this.getOrCreateQueue({
            ...options,
            playParams,
        })
            .then((queueResult) => {
                this.cloudQueueController.setQueue({
                    ...options,
                    playParams,
                    queueResult,
                })
            })
            .catch(this.errorHandler)
    }

    private preloadTracks(result: CreateQueueResult) {
        const edge = first(result.createQueue?.queue?.current?.edges)
        const curr = playableEntityToPlaylistEntry(edge?.node)
        if (curr) {
            // trackLoadOptions can be lazy (ValueProvider) — resolve
            // before handing to the player. For audio entries it's a
            // direct value and resolves synchronously.
            Promise.all([this.playerPromise, resolveValueProvider(curr.trackLoadOptions)])
                .then(([player, options]) => {
                    player.preload(options)
                })
                .catch(noop)
        }
    }

    private async createQueue(options: CreateCloudQueueOptions): Promise<CreateQueueResult> {
        // TODO: consider preloading track from options.playParams
        const result = await createCloudQueue(options)
        this.preloadTracks(result)
        return result
    }

    /**
     * Returns the play queue state for a specific playable entity within the queue.
     */
    playableEntityState(
        entity: ObservableValue<Maybe<PlayableEntity>>
    ): ObservableValue<PlayQueueState> {
        const { playlistController } = this
        const getCurrentState = () => {
            let newState: PlayQueueState
            const playlistCurrent = playlistController.current.value
            const entityPlayParams = entity.value?.playParams
            const sameContainer =
                entityPlayParams &&
                equalDeep(entityPlayParams.containerIds, this.playParams.value?.containerIds)
            // Same entity, same play container
            if (entity.value && sameContainer && entity.value.id === playlistCurrent?.entity.id) {
                if (!this.player?.hasMetadata) {
                    newState = PlayQueueState.LOADING
                } else {
                    if (this.player.paused) {
                        newState = PlayQueueState.PAUSED
                    } else {
                        newState = PlayQueueState.PLAYING
                    }
                }
            } else {
                newState = PlayQueueState.NOT_SET
            }
            return newState
        }

        return externalData(getCurrentState(), (setData) => {
            const disposer = createDisposer()
            const { add, dispose } = disposer
            const refresh = () => {
                setData(getCurrentState())
            }
            this.playerPromise
                .then((player) => {
                    if (disposer.disposed) return
                    add(onPlaybackStateChange(player, refresh))
                })
                .catch(noop) // Error reported in constructor handler
            add(playlistController.current.onData(refresh))
            add(entity.onData(refresh))
            return dispose
        })
    }
}

function onPlaybackStateChange(player: Player, refresh: () => void): Unsubscribe {
    const { add, dispose } = createDisposer()
    add(player.on('pause', refresh))
    add(player.on('play', refresh))
    add(player.on('ended', refresh))
    add(player.on('loadedMetadata', refresh))
    return dispose
}

export function createPlayQueueBinding(props: PlayQueueBindingOptions) {
    return new PlayQueueBinding(props)
}
